import React from 'react';

export const TOUR_STORAGE_PREFIX = 'svod_onboarding_done_';

export function isTourDone(userId) {
    try {
        return localStorage.getItem(TOUR_STORAGE_PREFIX + userId) === '1';
    } catch {
        return true;
    }
}

export function markTourDone(userId) {
    try {
        localStorage.setItem(TOUR_STORAGE_PREFIX + userId, '1');
    } catch {
        // Без localStorage обучение просто покажется снова при следующем входе.
    }
}

export function buildDashboardTourSteps({ tier, hasPolls }) {
    const isFree = !tier || tier === 'free';
    return [
        {
            id: 'welcome',
            title: 'Добро пожаловать в СВОД!',
            body: (
                <>
                    <p>За минуту покажем, как провести опрос: создать вопросы, запустить голосование и смотреть результаты в реальном времени.</p>
                    <p>Обучение можно пропустить и пройти позже — пункт «Обучение» в меню слева (на телефоне — под кнопкой ☰).</p>
                </>
            ),
        },
        {
            id: 'create',
            target: '[data-tour="create-poll"]',
            title: '1. Создайте опрос',
            body: (
                <>
                    <p>Укажите название и описание — после этого сразу откроется редактор, где вы добавите вопросы. Доступно 8 типов: выбор одного или нескольких вариантов, ранжирование, оценка звёздами (NPS), число, облако слов, выбор ячейки на картинке и тепловая карта кликов.</p>
                    <p>В вопрос с вариантами можно добавить <b>«Другое»</b> — участник напишет свой ответ, а на результатах ответы соберутся в облако фраз. Там же выберите <b>оформление для участников</b>: светлое или тёмное — для проектора в тёмном зале.</p>
                    {isFree && (
                        <div className="tour__note">На бесплатном тарифе добавьте все вопросы сразу: позже редактировать опрос нельзя.</div>
                    )}
                </>
            ),
        },
        ...(hasPolls ? [
            {
                id: 'card',
                target: '[data-tour="poll-card"]',
                optional: true,
                title: 'Карточка опроса',
                body: <p>Здесь название, число вопросов и статус. Все действия с опросом — кнопками внизу карточки.</p>,
            },
            {
                id: 'activate',
                target: '[data-tour="poll-activate"]',
                optional: true,
                title: '2. Активируйте опрос',
                body: (
                    <>
                        <p>Новый опрос создаётся неактивным — пока вы его не активируете, участники не смогут отвечать. Этой же кнопкой опрос останавливают.</p>
                        {isFree && <div className="tour__note">На бесплатном тарифе активным может быть только один опрос.</div>}
                    </>
                ),
            },
            {
                id: 'qr',
                target: '[data-tour="poll-qr"]',
                optional: true,
                title: '3. Покажите QR-код',
                body: <p>Два QR-кода: для ответа через Telegram-бота и для ответа на сайте, без установки приложений. Выведите их на экран или слайд — участники сканируют камерой телефона.</p>,
            },
            {
                id: 'links',
                target: '[data-tour="poll-links"]',
                optional: true,
                title: 'Ссылки на опрос и результаты',
                body: <p>Ссылки для участников (Telegram и сайт) можно отправить в чат или рассылку. Третья ссылка — страница результатов: откройте её на проекторе, графики обновляются сами по мере ответов.</p>,
            },
            {
                id: 'edit',
                target: '[data-tour="poll-edit"]',
                optional: true,
                title: 'Редактирование',
                body: <p>Добавляйте, меняйте и удаляйте вопросы, меняйте название и настройку перехода к результатам после ответа. Здесь же включается «Поделиться презентацией».{isFree ? ' Доступно на расширенном и корпоративном тарифах.' : ''}</p>,
            },
            {
                id: 'report',
                target: '[data-tour="poll-report"]',
                optional: true,
                title: '4. Получите отчёт',
                body: <p>Когда опрос закончится, нажмите «Отчёт»: опрос завершится, а на почту придёт сводка с цифрами по каждому вопросу, выводами и рекомендациями от ИИ.{isFree ? ' Доступно на расширенном и корпоративном тарифах.' : ''}</p>,
            },
            {
                id: 'more',
                target: '[data-tour="poll-more"]',
                optional: true,
                title: 'Ещё действия',
                body: <p>В меню «Ещё» — удаление опроса, а также действия, которые не поместились в ряд на узком экране (например, «Отчёт» или «Встроить»). Удалённый опрос освобождает место в лимите опросов тарифа.</p>,
            },
        ] : [
            {
                id: 'card-preview',
                title: 'Что дальше',
                body: (
                    <>
                        <p>Созданный опрос появится здесь карточкой с кнопками:</p>
                        <ul>
                            <li><b>Активировать</b> — открыть приём ответов;</li>
                            <li><b>QR-код</b> и <b>Ссылки</b> — пригласить участников и открыть результаты;</li>
                            <li><b>Редактировать</b> — изменить вопросы;</li>
                            <li><b>Отчёт</b> — завершить опрос и получить сводку на почту.</li>
                        </ul>
                    </>
                ),
            },
        ]),
        {
            id: 'qa',
            target: '[data-tour="qa-link"]',
            optional: true,
            menuItem: true,
            title: 'Вопросы из зала',
            body: <p>Отдельно от опросов: создайте сессию, покажите QR-код — участники задают вопросы спикеру и голосуют за интересные, а вы выводите их на экран.{isFree ? ' Доступно на расширенном и корпоративном тарифах.' : ''}</p>,
        },
        {
            id: 'presentations',
            target: '[data-tour="presentations-link"]',
            optional: true,
            menuItem: true,
            title: 'Ваши презентации',
            body: (
                <>
                    <p>Загрузите презентацию выступления (PDF, PPTX или ODP до 25 МБ) и включите «Поделиться презентацией» в опросе или сессии «Вопросы из зала». Участники оставят почту после ответа или отсканируют QR-код с экрана — и получат файл на почту.</p>
                    <p>Здесь же видно, к каким опросам подключён файл и сколько человек его запросили и скачали. Файл хранится 30 дней.{isFree ? ' Доступно на расширенном и корпоративном тарифах.' : ''}</p>
                </>
            ),
        },
        {
            id: 'profile',
            target: '[data-tour="profile-link"]',
            optional: true,
            title: 'Профиль и тариф',
            body: <p>Тариф и лимиты, оплата и смена пароля. Сколько опросов осталось по тарифу, видно и внизу бокового меню. Светлую или тёмную тему кабинета выберите, нажав на своё имя в правом верхнем углу.</p>,
        },
        {
            id: 'help',
            target: '[data-tour="help-link"]',
            optional: true,
            title: 'Справка всегда под рукой',
            body: <p>В справке — подробно о типах вопросов, результатах, «Вопросах из зала», презентациях, тарифах и ответы на частые вопросы. Пройти обучение заново можно пунктом «Обучение» в меню.</p>,
        },
    ];
}
